// The canonical-WAV uploader shared by voicemail greetings (RTE-14), routing prompts (migration 016) and voicemail
// drops: a picked or dropped file (one at a time; kept byte for byte when already canonical, otherwise converted here)
// or an in-page microphone recording, after a three-second countdown, converted to PCM 16-bit mono 8 kHz, 1 to 60 s,
// at most 1 MiB; a local preview before upload; and the server's refusal next to the Upload button. The caller
// supplies the words and what Upload does; nothing is sent until Upload. Every action takes a new operation number,
// so a file read, recording or upload that settles after a newer action (or after unmount) is ignored.
import { type ChangeEvent, type DragEvent, type ReactNode, useEffect, useId, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { Circle, FileAudio, Mic, Pause, Play, Square, Upload } from 'lucide-react';
import { formatDuration } from '../../lib/format.ts';
import { Icon } from '../../ui/Icon.tsx';
import { useAnnouncer } from '../../ui/Announcer.tsx';
import { GreetingRecorder, type PrepareResult, type Prepared, RECORDER_ERROR_COPY, type RecorderEnv, type RecorderSnapshot, UNAVAILABLE_COPY, browserEnv, prepareFile, recorderUnavailable } from './recorder.ts';
import { MAX_SOURCE_BYTES, PREPARE_COPY } from './wav.ts';

/** The server's refusal of an upload: the shape both voicemail and routing faults share. */
export interface UploadFault {
  title: string;
  code: string;
  requestId: string | null;
  status: number;
  serverMessage: string;
  outcomeUnknown: boolean;
}

/** `fault: null` means the caller refused before sending and shows its own reason (for example a missing name). */
export type UploadResult = { ok: true } | { ok: false; fault: UploadFault | null };

export interface WavUploaderCopy {
  /** The fieldset legend ("Add a greeting"). */
  legend: string;
  /** What the audio is, in the announcements ("greeting", "prompt"). */
  noun: string;
  /** The Upload button ("Upload greeting"). */
  uploadLabel: string;
  fileHint: string;
  /** Overrides for the client-side refusal copy (wav.ts PREPARE_COPY keys, plus multiple_files). */
  prepareCopy?: Readonly<Record<string, string>>;
}

export const kb = (bytes: number) => `${Math.max(1, Math.round(bytes / 1024))} KB`;

/** Refusals the uploader makes itself, before reading anything. */
export const UPLOADER_COPY: Readonly<Record<string, string>> = Object.freeze({
  multiple_files: 'Choose one audio file at a time.',
});
export const DROP_HINT = 'You can also drag and drop one audio file here.';

/** The words for a client-side refusal: the caller's override, then the shared copy, then "not audio". */
export function refusalText(overrides: WavUploaderCopy['prepareCopy'], code: string): string {
  return overrides?.[code] ?? PREPARE_COPY[code] ?? UPLOADER_COPY[code] ?? overrides?.not_audio ?? PREPARE_COPY.not_audio!;
}

/** onUpload threw instead of answering: whether anything was saved isn't known. */
export const UPLOAD_THREW: UploadFault = {
  title: 'The upload didn’t finish. Check whether it was saved before trying again.',
  code: 'upload_failed',
  requestId: null,
  status: 0,
  serverMessage: '',
  outcomeUnknown: true,
};

type Politeness = 'polite' | 'assertive';

/** A picked or dropped file as the uploader reads it (a File satisfies it). */
export interface SourceFile { name: string; size?: number; arrayBuffer(): Promise<ArrayBuffer> }

/** Exactly one file, or why not. Nothing is read here. */
export function pickOne<T>(files: ArrayLike<T> | null | undefined): { kind: 'none' } | { kind: 'one'; file: T } | { kind: 'many' } {
  if (!files || files.length === 0) return { kind: 'none' };
  if (files.length > 1) return { kind: 'many' };
  const file = files[0];
  return file === undefined ? { kind: 'none' } : { kind: 'one', file };
}

/** What to announce when the recorder moves from one state to another, or null. Nothing is said before the microphone is allowed. */
export function recorderAnnouncement(prev: RecorderSnapshot['state'], next: RecorderSnapshot, noun: string): { text: string; politeness: Politeness } | null {
  if (next.state === prev) return null;
  if (next.state === 'countdown') return { text: `Get ready. Recording your ${noun} in ${next.remainingSeconds} seconds.`, politeness: 'polite' };
  if (next.state === 'recording') return { text: `Recording. Speak your ${noun}, then stop.`, politeness: 'polite' };
  if (next.state === 'error') return { text: RECORDER_ERROR_COPY[next.code], politeness: 'assertive' };
  return null;
}

/** What an uploader action touches. The refs are the component's own; tests pass plain objects. */
export interface UploaderCtx {
  opGen: { current: number };
  mounted: { current: boolean };
  decode: RecorderEnv['decode'];
  prepareText: (code: string) => string;
  announce: (message: string, politeness?: Politeness) => void;
  setPrepared: (prepared: Prepared | null) => void;
  setProblem: (problem: string | null) => void;
  setReading: (reading: boolean) => void;
  setUploading: (uploading: boolean) => void;
  setServerFault: (fault: UploadFault | null) => void;
  /** Empties the file input, so choosing the same file again fires onChange. */
  clearInput: () => void;
}

/** Starts a new action: whatever an earlier one still has in flight is ignored when it settles, and "Reading the file" ends. */
export function beginOp(ctx: UploaderCtx): number {
  ctx.opGen.current += 1;
  ctx.setReading(false);
  return ctx.opGen.current;
}
const isCurrent = (ctx: UploaderCtx, gen: number) => ctx.mounted.current && gen === ctx.opGen.current;

/** Picked or dropped files: exactly one is read; more than one is refused without reading any. */
export async function ingestFiles(files: ArrayLike<SourceFile> | null | undefined, ctx: UploaderCtx): Promise<void> {
  const one = pickOne(files);
  if (one.kind === 'none') return;
  if (one.kind === 'one') return ingestFile(one.file, ctx);
  beginOp(ctx);
  ctx.setPrepared(null);
  ctx.setServerFault(null);
  ctx.clearInput();
  const text = ctx.prepareText('multiple_files');
  ctx.setProblem(text);
  ctx.announce(text, 'assertive');
}

/** Reads and prepares one file. A file over MAX_SOURCE_BYTES is refused before it is read into memory. */
export async function ingestFile(file: SourceFile, ctx: UploaderCtx): Promise<void> {
  const gen = beginOp(ctx);
  ctx.setProblem(null);
  ctx.setServerFault(null);
  ctx.setPrepared(null);
  let result: PrepareResult;
  if (typeof file.size === 'number' && file.size > MAX_SOURCE_BYTES) {
    result = { ok: false, code: 'file_too_big' };
  } else {
    ctx.setReading(true);
    try {
      const bytes = new Uint8Array(await file.arrayBuffer());
      if (!isCurrent(ctx, gen)) return;
      result = await prepareFile(bytes, file.name, ctx.decode);
    } catch {
      result = { ok: false, code: 'not_audio' };
    } finally {
      if (isCurrent(ctx, gen)) ctx.setReading(false);
    }
  }
  if (!isCurrent(ctx, gen)) return;
  if (result.ok) {
    ctx.setPrepared(result.prepared);
    ctx.announce(`${file.name} is ready: ${formatDuration(result.prepared.durationMs)}.`);
  } else {
    const text = ctx.prepareText(result.code);
    ctx.setProblem(text);
    ctx.announce(text, 'assertive');
    ctx.clearInput();
  }
}

/** Sends the prepared audio once. A completion that arrives after unmount or a newer action is ignored. */
export async function uploadPrepared(prepared: Prepared | null, uploading: boolean, onUpload: (wav: Uint8Array<ArrayBuffer>) => Promise<UploadResult>, ctx: UploaderCtx): Promise<void> {
  if (!prepared || uploading) return;
  const gen = ctx.opGen.current;
  const wav = prepared.wav;
  ctx.setUploading(true);
  ctx.setServerFault(null);
  let result: UploadResult | null = null;
  try {
    result = await onUpload(wav);
  } catch {
    result = null;
  } finally {
    // Only one upload runs at a time, so the busy state ends with it even when its result is stale; otherwise the
    // controls it disables would stay disabled.
    if (ctx.mounted.current) ctx.setUploading(false);
  }
  if (!isCurrent(ctx, gen)) return;
  if (!result) {
    ctx.setServerFault(UPLOAD_THREW);
    ctx.announce(UPLOAD_THREW.title, 'assertive');
  } else if (result.ok) {
    ctx.setPrepared(null);
    ctx.clearInput();
  } else if (result.fault) {
    ctx.setServerFault(result.fault);
    ctx.announce(result.fault.title, 'assertive');
  }
}

const hasFiles = (event: DragEvent) => Array.from(event.dataTransfer?.types ?? []).includes('Files') || (event.dataTransfer?.files?.length ?? 0) > 0;

export function WavUploader({ copy, env: injected, onUpload, children }: { copy: WavUploaderCopy; env: RecorderEnv | undefined; onUpload: (wav: Uint8Array<ArrayBuffer>) => Promise<UploadResult>; children?: ReactNode }) {
  const uid = useId().replace(/:/g, '');
  const { announce } = useAnnouncer();
  const env = useMemo(() => injected ?? browserEnv(), [injected]);
  const [prepared, setPrepared] = useState<Prepared | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [reading, setReading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [serverFault, setServerFault] = useState<UploadFault | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const opGenRef = useRef(0);
  const mountedRef = useRef(true);
  const recordGenRef = useRef(-1);
  const dragDepth = useRef(0);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);
  const recorder = useMemo(() => new GreetingRecorder(env, next => {
    // A file picked or dropped, Discard, Cancel or unmount since this recording started: its result is stale.
    if (!mountedRef.current || recordGenRef.current !== opGenRef.current) return;
    setPrepared(next);
    setProblem(null);
    setServerFault(null);
    announce(`Recording ready: ${formatDuration(next.durationMs)}. Listen to it, then upload.`);
  }), [env, announce]);
  useEffect(() => () => recorder.dispose(), [recorder]);
  const rec = useSyncExternalStore(recorder.subscribe, recorder.getSnapshot, recorder.getSnapshot);
  const prevState = useRef(rec.state);
  useEffect(() => {
    const said = recorderAnnouncement(prevState.current, rec, copy.noun);
    prevState.current = rec.state;
    if (said) announce(said.text, said.politeness);
  }, [rec, copy.noun, announce]);
  const unavailable = recorderUnavailable(env);
  const prepareText = (code: string) => refusalText(copy.prepareCopy, code);
  const ctx: UploaderCtx = {
    opGen: opGenRef, mounted: mountedRef, decode: env.decode, prepareText, announce,
    setPrepared, setProblem, setReading, setUploading, setServerFault,
    clearInput: () => { if (fileRef.current) fileRef.current.value = ''; },
  };

  const recording = rec.state === 'recording' || rec.state === 'countdown' || rec.state === 'requesting' || rec.state === 'processing';
  const busy = uploading || recording;
  const pick = (event: ChangeEvent<HTMLInputElement>) => void ingestFiles(event.target.files, ctx);
  const upload = () => void uploadPrepared(prepared, uploading, onUpload, ctx);
  const discard = () => {
    beginOp(ctx);
    setPrepared(null);
    setServerFault(null);
    ctx.clearInput();
  };
  const startRecording = () => {
    recordGenRef.current = beginOp(ctx);
    setProblem(null);
    setServerFault(null);
    setPrepared(null);
    ctx.clearInput();
    recorder.reset();
    // Announced when the recorder reaches the countdown, then recording: never before the microphone is allowed.
    void recorder.start();
  };
  const cancelRecording = () => {
    recorder.cancel();
    beginOp(ctx);
    announce('Recording cancelled.');
  };
  const stopRecording = () => {
    recorder.stop();
    announce('Recording stopped. Converting…');
  };

  // Drag and drop of one file onto the uploader; the file input stays the keyboard and click route. The depth
  // counter keeps the highlight while the pointer crosses child elements.
  const onDragEnter = (event: DragEvent<HTMLFieldSetElement>) => {
    if (!hasFiles(event)) return;
    event.preventDefault();
    dragDepth.current += 1;
    if (!busy) setDragOver(true);
  };
  const onDragOver = (event: DragEvent<HTMLFieldSetElement>) => {
    if (!hasFiles(event)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = busy ? 'none' : 'copy';
  };
  const onDragLeave = (event: DragEvent<HTMLFieldSetElement>) => {
    if (!hasFiles(event)) return;
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragOver(false);
  };
  const onDrop = (event: DragEvent<HTMLFieldSetElement>) => {
    if (!hasFiles(event)) return;
    event.preventDefault();
    dragDepth.current = 0;
    setDragOver(false);
    if (busy) return;
    void ingestFiles(event.dataTransfer.files, ctx);
  };

  const errorId = `${uid}-error`;
  return (
    <fieldset className={`field-group vm-upload${dragOver ? ' is-dragover' : ''}`} aria-describedby={problem ? errorId : undefined} onDragEnter={onDragEnter} onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}>
      <legend>{copy.legend}</legend>
      {children}
      <div className="field vm-dropzone">
        <label htmlFor={`${uid}-file`}><Icon icon={FileAudio} /> Choose an audio file</label>
        <input ref={fileRef} id={`${uid}-file`} type="file" accept="audio/wav,audio/x-wav,audio/wave,.wav,audio/*" disabled={busy} aria-describedby={`${uid}-file-hint`} onChange={pick} />
        <p id={`${uid}-file-hint`} className="field-hint">{copy.fileHint} {DROP_HINT}</p>
      </div>
      {reading ? <p className="field-hint" role="status">Reading the file…</p> : null}

      <RecorderPanel rec={rec} unavailable={unavailable} noun={copy.noun} disabled={uploading || reading} onRecord={startRecording} onStop={stopRecording} onCancel={cancelRecording} />

      {problem ? <p id={errorId} className="field-error" role="alert">{problem}</p> : null}

      {prepared ? (
        <div className="vm-prepared">
          <p>
            <strong>Ready to upload:</strong> {prepared.source === 'recording' ? 'your recording' : prepared.name ?? 'the chosen file'}, {formatDuration(prepared.durationMs)}
            {prepared.converted ? ', converted here to 8 kHz mono WAV' : ', already in the phone format'} ({kb(prepared.wav.byteLength)}).
          </p>
          <div className="form-actions">
            <LocalPreview wav={prepared.wav} />
            <button type="button" className="btn btn-quiet" onClick={discard} disabled={uploading}>Discard</button>
            <button type="button" className="btn btn-primary" onClick={upload} disabled={uploading} aria-busy={uploading || undefined} aria-describedby={serverFault ? `${uid}-server` : undefined}>
              <Icon icon={Upload} /> {uploading ? 'Uploading…' : copy.uploadLabel}
            </button>
          </div>
          {serverFault ? <UploadRefusal id={`${uid}-server`} fault={serverFault} /> : null}
        </div>
      ) : null}
    </fieldset>
  );
}

/** The microphone controls for each recorder state: why it isn't offered, the countdown, the running recording, or the Record button. */
export function RecorderPanel({ rec, unavailable, noun, disabled, onRecord, onStop, onCancel }: {
  rec: RecorderSnapshot;
  unavailable: ReturnType<typeof recorderUnavailable>;
  noun: string;
  disabled: boolean;
  onRecord: () => void;
  onStop: () => void;
  onCancel: () => void;
}) {
  const cancel = <button type="button" className="btn btn-small btn-quiet" onClick={onCancel}>Cancel</button>;
  return (
    <div className="vm-recorder">
      {unavailable ? (
        <p className="field-hint">{UNAVAILABLE_COPY[unavailable]}</p>
      ) : rec.state === 'recording' ? (
        <p className="vm-rec-line">
          <span className="vm-rec-dot" aria-hidden="true"><Icon icon={Circle} size="0.9em" /></span>
          <span className="num" aria-live="off">Recording {formatDuration(rec.elapsedMs)} of 1:00</span>
          <button type="button" className="btn btn-small btn-primary" onClick={onStop}><Icon icon={Square} /> Stop recording</button>
          {cancel}
        </p>
      ) : rec.state === 'countdown' ? (
        <div className="vm-rec-line vm-countdown">
          <p className="vm-countdown-text" role="status" aria-atomic="true"><span className="num vm-countdown-n">Starting in {rec.remainingSeconds}…</span> Get ready to speak your {noun}.</p>
          {cancel}
        </div>
      ) : rec.state === 'requesting' ? (
        <div className="vm-rec-line">
          <p className="field-hint" role="status">Allow microphone access in your browser to start recording…</p>
          {cancel}
        </div>
      ) : rec.state === 'processing' ? (
        <p className="field-hint" role="status">Converting the recording to the phone format…</p>
      ) : (
        <p>
          <button type="button" className="btn btn-small" disabled={disabled} onClick={onRecord}>
            <Icon icon={Mic} /> Record with your microphone
          </button>
          <span className="field-hint"> Up to 60 seconds, after a 3-second countdown. Recording stops by itself at 60 seconds.</span>
        </p>
      )}
      {rec.state === 'error' ? <p className="field-error" role="alert">{RECORDER_ERROR_COPY[rec.code]}</p> : null}
    </div>
  );
}

/** The server's refusal of an upload (413, 415, 422 and others), next to the Upload button: our plain copy and the server's own reason. */
export function UploadRefusal({ fault, id }: { fault: UploadFault; id?: string }) {
  return (
    <div id={id} className="vm-refusal" role="alert">
      <p className="field-error">{fault.title}</p>
      {fault.serverMessage && fault.serverMessage !== fault.title && !fault.outcomeUnknown ? <p>The server said: {fault.serverMessage}</p> : null}
      <p className="meta">Code <span className="mono">{fault.code}</span>{fault.status ? `, HTTP ${fault.status}` : ''}{fault.requestId ? <>, reference <span className="mono">{fault.requestId}</span></> : null}</p>
    </div>
  );
}

/** Plays the prepared audio from memory before upload (an object URL, revoked when it changes). */
export function LocalPreview({ wav }: { wav: Uint8Array<ArrayBuffer> }) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (typeof URL.createObjectURL !== 'function') return;
    const next = URL.createObjectURL(new Blob([wav], { type: 'audio/wav' }));
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [wav]);
  const toggle = () => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) void el.play().catch(() => setPlaying(false));
    else el.pause();
  };
  return (
    <>
      <audio ref={audioRef} src={url ?? undefined} preload="auto" onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => setPlaying(false)} />
      <button type="button" className="btn" onClick={toggle} disabled={!url}><Icon icon={playing ? Pause : Play} /> {playing ? 'Pause' : 'Listen'}</button>
    </>
  );
}
